import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import req from 'utils/req';
import { useAccount } from '~/authentication/store';
import { HOST_STATUS } from '~/constants';

type HostStatusType = {
  status: { type: string };
  reason?: string;
};

type HostStatusResponse = {
  status: { type: string };
  id: string;
  firstAcceptedAt: string;
  content: { outOfOfficeReason: string };
};

const HostQueryKeys = ['host', 'host-status'];
const getHostStatus = (id: string): Promise<HostStatusType> =>
  req(`${URL_API_HOST}/api/v1/portfolio/host/${id}/status`).get();
const setHostStatus = ({
  id,
  body,
}: {
  id: string;
  body: HostStatusType;
}): Promise<HostStatusResponse> =>
  req(`${URL_API_HOST}/api/v1/portfolio/host/${id}/status`).put(body);

function useHostStatus() {
  const queryClient = useQueryClient();
  const { account } = useAccount();
  const accountId = account?.id;

  const queryData = useQuery(
    [...HostQueryKeys, accountId],
    async () => {
      try {
        return await getHostStatus(accountId);
      } catch (error) {
        console.error('Error fetching host status', error);
        return { status: { type: HOST_STATUS.ACTIVE } };
      }
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
    },
  );

  const mutation = useMutation(
    (body: HostStatusType) => setHostStatus({ id: accountId, body }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([...HostQueryKeys, data.id], {
          refetchType: 'active',
        });
        queryClient.setQueryData([...HostQueryKeys, data.id], () => ({
          status: data.status,
          reasons: data.content.outOfOfficeReason,
        }));
      },
      retry: 3,
    },
  );

  return {
    ...queryData,
    actions: {
      setHostStatus: mutation.mutateAsync,
    },
    isLoading: queryData?.isLoading || mutation.isLoading,
  };
}

export default useHostStatus;
